import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe } from "lucide-react";
import { useLanguage } from "@/i18n";
import Logo from "@/components/Logo";

const LANGS = ["fr", "de", "en"];

const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const links = [
    { to: "/", label: t.nav.home, testid: "nav-home-link" },
    { to: "/it", label: t.nav.it, testid: "nav-it-link" },
    { to: "/transport", label: t.nav.transport, testid: "nav-transport-link" },
    { to: "/contact", label: t.nav.contact, testid: "nav-contact-link" },
  ];

  const linkCls = ({ isActive }) =>
    `px-4 py-2 text-sm font-medium tracking-wide transition-colors duration-200 ${
      isActive ? "text-cyan-300" : "text-slate-300 hover:text-white"
    }`;

  return (
    <header data-testid="nav-header" className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 bg-[#0A0D14]/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-2.5" onClick={() => setOpen(false)}>
          <Logo className="h-8 w-8" />
          <span className="text-lg font-extrabold tracking-tight text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            iTranspro
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={l.testid} className={linkCls} end={l.to === "/"}>
              {l.label}
            </NavLink>
          ))}
          <div className="ml-3 flex items-center gap-1 rounded-full border border-white/10 bg-white/5 p-1" data-testid="language-switcher-btn">
            <Globe className="ml-1.5 h-3.5 w-3.5 text-slate-400" />
            {LANGS.map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}-btn`}
                onClick={() => setLang(l)}
                className={`rounded-full px-2.5 py-1 text-xs font-bold uppercase transition-colors duration-200 ${
                  lang === l ? "bg-cyan-400 text-slate-950" : "text-slate-400 hover:text-white"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </nav>

        <div className="flex items-center gap-2 md:hidden">
          <div className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 p-1" data-testid="language-switcher-mobile">
            {LANGS.map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}-mobile-btn`}
                onClick={() => setLang(l)}
                className={`rounded-full px-2 py-1 text-[10px] font-bold uppercase ${
                  lang === l ? "bg-cyan-400 text-slate-950" : "text-slate-400"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <button data-testid="mobile-menu-toggle-btn" onClick={() => setOpen(!open)} className="p-2 text-white" aria-label="Menu">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-t border-white/10 bg-[#0A0D14] md:hidden"
          >
            <div className="flex flex-col px-4 py-3">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => setOpen(false)}
                  className={`border-b border-white/5 py-3.5 text-base font-medium ${
                    location.pathname === l.to ? "text-cyan-300" : "text-slate-200"
                  }`}
                  end={l.to === "/"}
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
